import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import Modal from './Modal'
import { formatFullDate } from '../api/calendar'
import { markWhatsNewSeen, type WhatsNewEntry } from '../api/whatsNew'
import {
  closeWhatsNewThisLoad,
  useDialogsStandDown,
  whatsNewClosedThisLoad,
  whatsNewThisLoad,
} from '../dialogStandDown'
import { SESSION_KEY, useSession } from '../useSession'

/**
 * What changed since she last opened the app, for her (D-731).
 *
 * `asked`, the owner: *"a what's new type of page when users open the app for the
 * first time after we push a new version ... broken down by what new features they
 * will see based on their account."* Large new features only; the server has
 * already chosen the entries she matches.
 *
 * **Shown on the load, never on a refetch.** The list is taken from the first
 * session this page load receives for her account and never re-read - held beside
 * the load's other facts, so a remount inside the load cannot re-read it either - and
 * a deploy landing mid-evening does not throw a dialog at a teacher moving from one
 * register to the next. It waits for her next load.
 *
 * **The welcome goes first** - the server sends no entries until the welcome is
 * dismissed - and both stand down after a notification link or once attendance work
 * has been opened (`useDialogsStandDown`).
 *
 * **Marked seen on dismissal, not on render** (D-651's reason), naming exactly the
 * entries shown: an entry deployed between this load and her tap was not shown, and
 * must not be marked. A failed write shows it again next load, the harmless way.
 */
export default function WhatsNewDialog() {
  const { session } = useSession()
  const queryClient = useQueryClient()
  const standDown = useDialogsStandDown()
  // On the page that lists the same entries, the dialog would only cover them.
  const onTheList = useLocation().pathname === '/whats-new'

  // Re-render once she dismisses; the fact itself lives with the load.
  const [, setDismissed] = useState(0)

  const seen = useMutation({
    mutationFn: markWhatsNewSeen,
    onSettled: () => queryClient.invalidateQueries({ queryKey: SESSION_KEY }),
  })

  const accountId = session?.accountId
  const entries: WhatsNewEntry[] = accountId ? whatsNewThisLoad(accountId, session?.whatsNew ?? []) : []

  if (!accountId || whatsNewClosedThisLoad(accountId)) return null
  if (standDown || onTheList || entries.length === 0 || session?.showWelcome === true) return null

  const dismiss = () => {
    closeWhatsNewThisLoad(accountId)
    setDismissed((n) => n + 1)
    seen.mutate(entries.map((e) => e.number))
  }

  return (
    <Modal
      onDismiss={dismiss}
      labelledBy="whats-new-title"
      panelClassName="flex max-h-[calc(100vh-2.5rem)] w-full max-w-[26rem] flex-col rounded-2xl border border-line bg-surface shadow-2xl"
    >
      <div className="min-h-0 overflow-y-auto px-5 pt-5">
        <p className="text-[11px] font-bold tracking-[0.1em] text-ink-3 uppercase">
          What&rsquo;s new &middot; {formatFullDate(entries[0]!.released)}
        </p>
        <h2 id="whats-new-title" className="mt-1 font-display text-xl">
          New in the app
        </h2>
        <p className="mt-0.5 text-[12.5px] text-ink-3">Here&rsquo;s what changed since you last opened it.</p>

        <ul className="mt-3 flex flex-col">
          {entries.map((entry) => (
            <li key={entry.number} className="flex gap-2.5 border-t border-line py-3 first:border-t-0 first:pt-1">
              <span
                aria-hidden="true"
                className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-lg bg-accent-soft text-accent-2"
              >
                <Sparkles size={15} strokeWidth={2.25} />
              </span>
              <div className="min-w-0">
                <h3 className="text-[13.5px] leading-snug font-semibold">{entry.title}</h3>
                <p className="mt-0.5 text-[12.5px] text-ink-2">{entry.body}</p>
                {entry.wherePath && (
                  <Link
                    to={entry.wherePath}
                    onClick={dismiss}
                    className="mt-1 inline-block text-[12.5px] font-bold text-accent"
                  >
                    {entry.whereLabel} &rarr;
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-2 border-t border-line px-5 py-4">
        <button
          type="button"
          onClick={dismiss}
          className="min-h-[44px] w-full rounded-lg border border-accent bg-accent-soft px-3 py-2 text-[13.5px] font-bold text-accent-2"
        >
          Got it
        </button>
        <Link
          to="/whats-new"
          onClick={dismiss}
          className="text-center text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          See everything new
        </Link>
      </div>
    </Modal>
  )
}
